import { CustomQueryOptions } from '@/types/query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { useDemoMode } from '@/context/DemoModeContext';
import {
  mockVolumes,
  mockSnapshots,
  mockSchedules,
  mockStorageClasses,
  mockVolumeActivityLogs,
  generateMetricsHistory,
  getVolumeById,
  getSnapshotsForVolume,
  getSchedulesForVolume,
  getActivityLogsForVolume,
  mockDelay,
} from '@/mock/gpu-instance/volume';
import {
  listVolumes,
  getVolume,
  createVolume,
  resizeVolume,
  attachVolume,
  detachVolume,
  cloneVolume,
  deleteVolume,
  getVolumeMetricsHistory,
  getVolumeActivityLogs,
  listSnapshots,
  createSnapshot,
  restoreSnapshot,
  deleteSnapshot,
  listSchedules,
  createSchedule,
  updateSchedule,
  toggleSchedule,
  deleteSchedule,
  listStorageClasses,
} from './volume';
import type {
  Volume,
  VolumeSnapshot,
  SnapshotSchedule,
  StorageClass,
  VolumeActivityLog,
  MetricsHistoryPoint,
  CreateVolumePayload,
  ResizeVolumePayload,
  AttachVolumePayload,
  DetachVolumePayload,
  CloneVolumePayload,
  CreateSnapshotPayload,
  RestoreSnapshotPayload,
  CreateSchedulePayload,
} from './volume-type';

const keyname = 'gpu-instance';
const volumeKey = 'volumes';
const snapshotKey = 'snapshots';
const scheduleKey = 'schedules';
const storageClassKey = 'storage-classes';

// ============ Volume Hooks ============

export const useListVolumes = (option?: CustomQueryOptions<Volume[]>) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, volumeKey, 'list', { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(500);
        return mockVolumes;
      }

      if (!token) {
        throw new Error('No token available');
      }
      const response = await listVolumes(token);
      return response.volumes;
    },
    enabled: isDemoMode || !!token,
    ...option,
  });
};

export const useGetVolume = (volumeId: string, option?: CustomQueryOptions<Volume>) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, volumeKey, volumeId, { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(300);
        const volume = getVolumeById(volumeId);
        if (!volume) throw new Error('Volume not found');
        return volume;
      }

      if (!token) {
        throw new Error('No token available');
      }
      return getVolume(token, volumeId);
    },
    enabled: (isDemoMode || !!token) && !!volumeId,
    ...option,
  });
};

export const useCreateVolume = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: CreateVolumePayload) => {
      if (isDemoMode) {
        await mockDelay(1000);
        return {
          success: true,
          message: 'Volume created successfully',
          volumeId: `vol-${Date.now()}`,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return createVolume(token, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
    },
  });
};

export const useResizeVolume = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: ResizeVolumePayload) => {
      if (isDemoMode) {
        await mockDelay(1500);
        return {
          success: true,
          message: 'Volume resize initiated',
          volumeId: payload.volumeId,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return resizeVolume(token, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey, variables.volumeId] });
    },
  });
};

export const useAttachVolume = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: AttachVolumePayload) => {
      if (isDemoMode) {
        await mockDelay(800);
        return {
          success: true,
          message: 'Volume attached successfully',
          volumeId: payload.volumeId,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return attachVolume(token, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey, variables.volumeId] });
    },
  });
};

export const useDetachVolume = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: DetachVolumePayload) => {
      if (isDemoMode) {
        await mockDelay(800);
        return {
          success: true,
          message: 'Volume detached successfully',
          volumeId: payload.volumeId,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return detachVolume(token, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey, variables.volumeId] });
    },
  });
};

export const useCloneVolume = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: CloneVolumePayload) => {
      if (isDemoMode) {
        await mockDelay(2000);
        return {
          success: true,
          message: 'Volume cloned successfully',
          volumeId: `vol-${Date.now()}`,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return cloneVolume(token, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
    },
  });
};

export const useDeleteVolume = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (volumeId: string) => {
      if (isDemoMode) {
        await mockDelay(1000);
        return {
          success: true,
          message: 'Volume deleted successfully',
          volumeId,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return deleteVolume(token, volumeId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
    },
  });
};

// ============ Volume Metrics Hooks ============

export const useVolumeMetricsHistory = (
  volumeId: string,
  hours: number = 24,
  option?: CustomQueryOptions<MetricsHistoryPoint[]>
) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, volumeKey, volumeId, 'metrics', hours, { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(400);
        return generateMetricsHistory(volumeId, hours);
      }

      if (!token) {
        throw new Error('No token available');
      }
      return getVolumeMetricsHistory(token, volumeId, hours);
    },
    enabled: (isDemoMode || !!token) && !!volumeId,
    refetchInterval: 60000, // Refresh every minute
    ...option,
  });
};

export const useVolumeActivityLogs = (
  volumeId: string,
  option?: CustomQueryOptions<VolumeActivityLog[]>
) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, volumeKey, volumeId, 'activity', { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(300);
        return getActivityLogsForVolume(volumeId);
      }

      if (!token) {
        throw new Error('No token available');
      }
      return getVolumeActivityLogs(token, volumeId);
    },
    enabled: (isDemoMode || !!token) && !!volumeId,
    ...option,
  });
};

// ============ Snapshot Hooks ============

export const useListSnapshots = (volumeId?: string, option?: CustomQueryOptions<VolumeSnapshot[]>) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, snapshotKey, 'list', volumeId, { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(400);
        if (volumeId) {
          return getSnapshotsForVolume(volumeId);
        }
        return mockSnapshots;
      }

      if (!token) {
        throw new Error('No token available');
      }
      const response = await listSnapshots(token, volumeId);
      return response.snapshots;
    },
    enabled: isDemoMode || !!token,
    ...option,
  });
};

export const useCreateSnapshot = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: CreateSnapshotPayload) => {
      if (isDemoMode) {
        await mockDelay(1500);
        return {
          success: true,
          message: 'Snapshot creation initiated',
          snapshotId: `snap-${Date.now()}`,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return createSnapshot(token, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [keyname, snapshotKey] });
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey, variables.volumeId] });
    },
  });
};

export const useRestoreSnapshot = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: RestoreSnapshotPayload) => {
      if (isDemoMode) {
        await mockDelay(2000);
        return {
          success: true,
          message: payload.restoreType === 'in-place'
            ? 'Snapshot restore initiated'
            : 'New volume creation from snapshot initiated',
          volumeId: payload.restoreType === 'new-volume' ? `vol-${Date.now()}` : undefined,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return restoreSnapshot(token, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, volumeKey] });
      queryClient.invalidateQueries({ queryKey: [keyname, snapshotKey] });
    },
  });
};

export const useDeleteSnapshot = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (snapshotId: string) => {
      if (isDemoMode) {
        await mockDelay(800);
        return {
          success: true,
          message: 'Snapshot deleted successfully',
          snapshotId,
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return deleteSnapshot(token, snapshotId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, snapshotKey] });
    },
  });
};

// ============ Schedule Hooks ============

export const useListSchedules = (volumeId?: string, option?: CustomQueryOptions<SnapshotSchedule[]>) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, scheduleKey, 'list', volumeId, { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(300);
        if (volumeId) {
          return getSchedulesForVolume(volumeId);
        }
        return mockSchedules;
      }

      if (!token) {
        throw new Error('No token available');
      }
      const response = await listSchedules(token, volumeId);
      return response.schedules;
    },
    enabled: isDemoMode || !!token,
    ...option,
  });
};

export const useCreateSchedule = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (payload: CreateSchedulePayload) => {
      if (isDemoMode) {
        await mockDelay(800);
        return {
          success: true,
          message: 'Schedule created successfully',
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return createSchedule(token, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, scheduleKey] });
    },
  });
};

export const useUpdateSchedule = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async ({ scheduleId, updates }: { scheduleId: string; updates: Partial<CreateSchedulePayload> }) => {
      if (isDemoMode) {
        await mockDelay(500);
        return {
          success: true,
          message: 'Schedule updated successfully',
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return updateSchedule(token, scheduleId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, scheduleKey] });
    },
  });
};

export const useToggleSchedule = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async ({ scheduleId, enabled }: { scheduleId: string; enabled: boolean }) => {
      if (isDemoMode) {
        await mockDelay(300);
        return {
          success: true,
          message: enabled ? 'Schedule enabled' : 'Schedule disabled',
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return toggleSchedule(token, scheduleId, enabled);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, scheduleKey] });
    },
  });
};

export const useDeleteSchedule = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      if (isDemoMode) {
        await mockDelay(500);
        return {
          success: true,
          message: 'Schedule deleted successfully',
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return deleteSchedule(token, scheduleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, scheduleKey] });
    },
  });
};

// ============ Storage Class Hooks ============

export const useListStorageClasses = (option?: CustomQueryOptions<StorageClass[]>) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, storageClassKey, 'list', { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(200);
        return mockStorageClasses;
      }

      if (!token) {
        throw new Error('No token available');
      }
      return listStorageClasses(token);
    },
    enabled: isDemoMode || !!token,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    ...option,
  });
};
